/**
 * Service: Referrals (B7)
 *
 * READ-ONLY from the client's point of view. A referral is earned server-side
 * when an invitee places their first order, and a discount is resolved
 * server-side at checkout. There is deliberately NO client-callable way to
 * grant, apply, or count discounts — the numbers here are reporting only.
 *
 * INVARIANT: nothing in this shape is a money balance. `discountsUnspent` is a
 * COUNT OF ENTITLEMENTS, not an amount the user "has". Never render it as money.
 */
import { supabase } from "@/integrations/supabase/client";

export interface ReferralInvitation {
  event_id: string;
  earned_at: string;
  invitee_name: string | null;
  reward: Record<string, unknown> | null;
  config_version: number | null;
  qualifying_order: string | null;
  consumed_order: string | null;
  consumed_at: string | null;
  consumed_amount_cents: number | null;
}

export interface ReferralSummary {
  code: string | null;
  referrals_made: number;
  discounts_earned: number;
  discounts_used: number;
  discounts_unspent: number;
  invitations: ReferralInvitation[];
}

function normalise(raw: unknown): ReferralSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    code: (r.code as string) ?? null,
    referrals_made: Number(r.referrals_made ?? 0),
    discounts_earned: Number(r.discounts_earned ?? 0),
    discounts_used: Number(r.discounts_used ?? 0),
    discounts_unspent: Number(r.discounts_unspent ?? 0),
    invitations: Array.isArray(r.invitations) ? (r.invitations as ReferralInvitation[]) : [],
  };
}

/** The signed-in user's own referral record. */
export async function getMyReferralSummary(): Promise<{ data: ReferralSummary | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("my_referral_summary");
  return { data: normalise(data), error: error ? new Error(error.message) : null };
}

/** Staff view for the CRM. Authorisation is enforced inside the RPC. */
export async function getReferralSummaryForUser(
  userId: string,
): Promise<{ data: ReferralSummary | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("referral_summary_for_user", { p_user_id: userId });
  return { data: normalise(data), error: error ? new Error(error.message) : null };
}

/** Mints (or returns) the caller's stable referral code. */
export async function getMyReferralCode(): Promise<{ code: string | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("my_referral_code");
  return { code: (data as string) ?? null, error: error ? new Error(error.message) : null };
}

/** Human wording for a reward, WITHOUT implying stored value. */
export function describeReward(reward: Record<string, unknown> | null | undefined): string {
  const form = String(reward?.form ?? "none");
  if (form === "percent") return `${Number(reward?.percent_off ?? 0)}% off a card fee`;
  if (form === "fixed") return `$${(Number(reward?.amount_off_cents ?? 0) / 100).toFixed(2)} off a card fee`;
  if (form === "free_after_n") return `Free card after ${Number(reward?.free_after_n ?? 0)} referrals`;
  return "No reward";
}

/**
 * B7 · THE AMOUNT THE SCREEN SHOWS IS THE AMOUNT STRIPE IS GIVEN.
 *
 * `create-card-payment` resolves the fee and any referral discount server-side
 * and asserts `assert_fee_only_amount` before creating the intent. The checkout
 * screen used to render the configured LIST fee, so a discounted order could
 * display one figure and authorise another. This reads the SAME quote, scoped to
 * the caller (`auth.uid()` inside the RPC — no user id crosses the wire).
 */
export type DiscountQuote = {
  feeCents: number;
  discountCents: number;
  amountCents: number;
  source: string;
  rewardForm: string;
};

export async function getMyDiscountQuote(
  programId: string,
  quantity = 1,
): Promise<{ data: DiscountQuote | null; error: Error | null }> {
  const { data, error } = await supabase.rpc("my_referral_discount_quote", {
    p_program_id: programId,
    p_quantity: quantity,
  });
  if (error) return { data: null, error: new Error(error.message) };
  const q = (data ?? {}) as Record<string, any>;
  if (typeof q.amount_cents !== "number") return { data: null, error: null };
  return {
    data: {
      feeCents: Number(q.fee_cents ?? 0),
      discountCents: Number(q.discount_cents ?? 0),
      amountCents: Number(q.amount_cents),
      source: String(q.source ?? "none"),
      rewardForm: String(q.reward_form ?? "none"),
    },
    error: null,
  };
}
